import { useState } from "react";
import type { CSSProperties } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import type { Deployment, DiffResult } from "@rebuild/shared";

function getLabelStyle(color: string | null): CSSProperties | undefined {
  if (!color) return undefined;

  const hex = color.startsWith("#") ? color.slice(1) : color;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return undefined;

  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

  return {
    backgroundColor: `#${hex}`,
    color: luminance > 0.62 ? "#111827" : "#ffffff",
  };
}

export function PrRow({
  pr,
  deployment,
  onDeploy,
}: {
  pr: DiffResult["pulls"][number];
  deployment: Deployment | null;
  onDeploy: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md overflow-hidden" style={{ border: "1px solid #9ca3af" }}>
      <div className="w-full flex items-center justify-between bg-white hover:bg-gray-50 px-3 py-2 transition-colors text-left">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
        >
          <svg
            className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-indigo-600 shrink-0">#{pr.number}</span>
          <span className="flex flex-1 flex-wrap items-center gap-1.5 min-w-0">
            <span className="text-sm text-gray-900 truncate min-w-0 max-w-full">{pr.title}</span>
            {(pr.labels ?? []).map((label) => {
              const labelStyle = getLabelStyle(label.color);

              return (
                <span
                  key={`${label.name}-${label.color ?? "none"}`}
                  title={label.name}
                  style={labelStyle}
                  className={`max-w-32 truncate rounded-full px-1.5 py-0.5 text-[11px] font-medium leading-none ${
                    labelStyle ? "" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {label.name}
                </span>
              );
            })}
          </span>
        </button>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <span className="text-xs text-gray-400">{pr.user}</span>
          <span className="text-xs text-gray-400">{new Date(pr.mergedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
          <button
            onClick={onDeploy}
            disabled={!deployment}
            title={deployment ? "Deploy through this row: includes this change and every older row below it" : "No deployable build found"}
            className="px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-md transition-colors"
          >
            {deployment ? "Deploy to here" : "No build"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="bg-gray-50 border-t border-gray-100 px-4 py-3">
          {pr.bodyHtml ? (
            <div
              className="text-sm text-gray-700 mb-3 max-h-[50rem] overflow-y-auto prose prose-sm prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:text-gray-900 prose-headings:mt-3 prose-headings:mb-1 prose-a:text-indigo-600 prose-code:text-gray-700 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-100 prose-pre:rounded-md prose-img:max-w-full prose-img:rounded-md max-w-none [&_video]:max-w-full [&_video]:rounded-md"
              dangerouslySetInnerHTML={{ __html: pr.bodyHtml }}
            />
          ) : pr.body ? (
            <div className="text-sm text-gray-700 mb-3 max-h-[50rem] overflow-y-auto prose prose-sm prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:text-gray-900 prose-headings:mt-3 prose-headings:mb-1 prose-a:text-indigo-600 prose-code:text-gray-700 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-100 prose-pre:rounded-md max-w-none">
              <Markdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  img: ({ src, alt, ...props }) => (
                    <img src={src} alt={alt ?? ""} className="max-w-full rounded-md my-2" loading="lazy" {...props} />
                  ),
                  video: ({ src, ...props }) => (
                    <video src={src} controls className="max-w-full rounded-md my-2" {...props as React.VideoHTMLAttributes<HTMLVideoElement>} />
                  ),
                }}
              >{pr.body}</Markdown>
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic mb-3">No description provided.</p>
          )}
          <a
            href={pr.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition-colors"
          >
            View on GitHub
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      )}
    </div>
  );
}
